import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, SlidersHorizontal, Save } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  cost_code: "Cost Code", description: "Description", uom: "UOM",
  std_rate: "Std $/hr", orig_qty: "Orig Qty", rev_qty: "Rev Qty",
  orig_hrs: "Orig Hrs", rev_hrs: "Rev Hrs", installed_qty: "Installed Qty",
  sage_hrs: "Sage hrs",
};
const NONE = "__none__";

export default function ProcoreFieldMapping() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keys, setKeys] = useState([]);
  const [appFields, setAppFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [syncedAt, setSyncedAt] = useState(null);

  useEffect(() => {
    api.get("/procore/field-mapping")
      .then((r) => {
        setKeys(r.data.keys || []);
        setAppFields(r.data.app_fields || Object.keys(FIELD_LABELS));
        setMapping(r.data.mapping || {});
        setSyncedAt(r.data.synced_at);
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const sampleFor = (key) => (keys.find((k) => k.key === key) || {}).sample;

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(mapping).forEach(([k, v]) => { if (v && v !== NONE) clean[k] = v; });
      await api.put("/procore/field-mapping", { mapping: clean });
      setMapping(clean);
      toast.success("Field mapping saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;

  return (
    <div className="max-w-3xl space-y-5" data-testid="field-mapping-page">
      <div>
        <h1 className="font-head text-2xl font-bold text-slate-900">Procore Field Mapping</h1>
        <p className="text-sm text-slate-500">
          Map each app field to a key from the raw Procore budget JSON.
          {syncedAt ? ` Keys from data synced ${new Date(syncedAt).toLocaleString()}.` : " Sync a project first to discover keys."}
        </p>
      </div>

      {keys.length === 0 ? (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          No synced Procore data yet. Go to Projects and click "Sync data" on a linked project, then return here to map fields.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm divide-y divide-slate-100" data-testid="mapping-list">
          {appFields.map((field) => {
            const selectedKey = mapping[field] || NONE;
            return (
              <div key={field} className="grid sm:grid-cols-[160px_1fr] gap-3 items-center p-3" data-testid={`mapping-row-${field}`}>
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-[#E8791A]" />
                  <span className="text-sm font-medium text-slate-800">{FIELD_LABELS[field] || field}</span>
                </div>
                <div>
                  <Select value={selectedKey} onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v }))}>
                    <SelectTrigger className="h-9" data-testid={`mapping-select-${field}`}><SelectValue placeholder="Select a Procore key" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value={NONE}>— Unmapped —</SelectItem>
                      {keys.map((k) => (
                        <SelectItem key={k.key} value={k.key} data-testid={`mapping-option-${field}-${k.key}`}>
                          {k.key}{k.sample !== "" ? `  ·  e.g. ${k.sample}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedKey !== NONE && (
                    <div className="text-[11px] text-slate-400 mt-1">Sample: <span className="font-mono">{sampleFor(selectedKey) || "—"}</span></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Button onClick={save} disabled={saving || keys.length === 0} className="bg-[#E8791A] hover:bg-[#D66A0F] text-white" data-testid="save-mapping-button">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save mapping
      </Button>
    </div>
  );
}
